"use client";

import { Icon } from "@/components/ui/icon";
import { formatShortDate } from "@/lib/date/format-short-date";
import { useInvoices } from "../hooks/use-subscription";

export function InvoiceHistory() {
  const { data: invoices, isLoading } = useInvoices();

  if (isLoading) return <InvoiceHistorySkeleton />;

  return (
    <div className="rounded-lg border border-white/10 bg-white/2">
      <div className="px-5 py-4">
        <h3 className="text-sm font-semibold text-heading">Invoices</h3>
      </div>

      {!invoices || invoices.length === 0 ? (
        <div className="border-t border-white/10 px-5 py-8 text-center">
          <div className="flex flex-col items-center gap-2">
            <div className="size-10 rounded-full bg-white/4 flex items-center justify-center">
              <Icon
                name="IconFileText"
                size={18}
                className="text-muted-foreground"
              />
            </div>
            <span className="text-sm text-muted-foreground">
              No invoices yet
            </span>
          </div>
        </div>
      ) : (
        <div className="border-t border-white/10">
          {/* Table header */}
          <div className="px-5 py-2.5 grid grid-cols-[1fr_auto_auto_auto] gap-6 text-xs font-medium text-muted-foreground uppercase tracking-wider max-md:hidden">
            <span>Date</span>
            <span className="w-20 text-right">Amount</span>
            <span className="w-16 text-center">Status</span>
            <span className="w-20 text-right">PDF</span>
          </div>
          <div className="divide-y divide-white/6">
            {invoices.map((invoice) => (
              <InvoiceRow key={invoice.id} invoice={invoice} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

interface InvoiceRowProps {
  invoice: {
    id: string;
    amount_due: number;
    amount_paid: number;
    status: string;
    created: number;
    invoice_pdf: string | null;
  };
}

function InvoiceRow({ invoice }: InvoiceRowProps) {
  const date = new Date(invoice.created * 1000);
  const formattedDate = formatShortDate(date);

  const amount = (invoice.amount_due / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

  const statusConfig: Record<string, { className: string; label: string }> = {
    paid: { className: "bg-emerald-500/10 text-emerald-400", label: "Paid" },
    open: { className: "bg-amber-500/10 text-amber-400", label: "Open" },
    draft: {
      className: "bg-white/4 text-muted-foreground",
      label: "Draft",
    },
    uncollectible: {
      className: "bg-red-500/10 text-red-400",
      label: "Failed",
    },
    void: {
      className: "bg-white/4 text-muted-foreground",
      label: "Void",
    },
  };

  const status = statusConfig[invoice.status] ?? {
    className: "bg-white/4 text-muted-foreground",
    label: invoice.status,
  };

  return (
    <div className="px-5 py-3 grid grid-cols-[1fr_auto_auto_auto] gap-6 items-center max-md:flex max-md:flex-wrap max-md:justify-between max-md:gap-3">
      <span className="text-sm text-foreground">{formattedDate}</span>
      <span className="w-20 text-right text-sm font-medium text-heading">
        {amount}
      </span>
      <span className="w-16 flex justify-center">
        <span
          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
        >
          {invoice.status === "paid" && (
            <Icon name="IconCheckmark1Small" size={10} />
          )}
          {status.label}
        </span>
      </span>
      <span className="w-20 flex justify-end">
        {invoice.invoice_pdf ? (
          <a
            href={invoice.invoice_pdf}
            target="_blank"
            rel="noopener noreferrer"
            className="h-7 px-2.5 rounded text-xs font-medium bg-white/4 border border-white/15 text-white/70 hover:text-white hover:bg-white/8 transition-colors cursor-pointer inline-flex items-center gap-1 shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05),inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]"
          >
            <Icon name="IconArrowUpRight" size={12} />
            PDF
          </a>
        ) : (
          <span className="text-xs text-muted-foreground">N/A</span>
        )}
      </span>
    </div>
  );
}

function InvoiceHistorySkeleton() {
  return (
    <div className="rounded-lg border border-white/10 bg-white/2">
      <div className="px-5 py-4">
        <div className="h-4 w-16 rounded bg-white/6 animate-pulse" />
      </div>
      <div className="border-t border-white/10">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="px-5 py-3 flex items-center justify-between border-b border-white/6 last:border-0"
          >
            <div className="h-3.5 w-24 rounded bg-white/6 animate-pulse" />
            <div className="h-3.5 w-16 rounded bg-white/4 animate-pulse" />
            <div className="h-5 w-12 rounded-full bg-white/4 animate-pulse" />
            <div className="h-7 w-14 rounded bg-white/4 animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  );
}
